<script lang="ts" setup>
import type { FormState } from "~~/models/forms";
import type { PlanktonTextSuggestion } from "~~/server/plankton";
import { useDebounceFn } from "@vueuse/core";

const { mode } = defineProps<{ mode: "header" | "page" }>();

const { t } = useI18n();
const localePath = useLocalePath();
const { trackCustomEvent } = useGtmTracking();

const searchTerm = useSearchTerm(`search-term-${mode}`);
const searchTermStatus = ref<FormState>("idle");

const searchForm = ref(null);
const searchTermInput = ref(null);
const searchSuggestionsContainer = ref(null);
const isOpenSearchSuggestions = ref(false);
onClickOutside(searchForm, _ => isOpenSearchSuggestions.value = false);

const searchSuggestionStatus = ref<FormState>("idle");
const searchSuggestionList = ref<Array<any>>([]);
const searchSuggestionSelectedIndex = ref(-1);
const queryLength = computed(() => searchTerm.value.length);

async function fetchData() {
  try {
    searchSuggestionList.value = [];
    isOpenSearchSuggestions.value = false;

    if (queryLength.value < 3) {
      return;
    }

    searchSuggestionStatus.value = "pending";

    const searchParameter = searchTerm.value;
    const data = await $fetch<{ textSuggestions: Array<PlanktonTextSuggestion> }>("/api/ecom/search-suggestions/autocomplete", {
      query: { term: searchParameter },
    });

    if (data && data.textSuggestions?.length > 0) {
      searchSuggestionList.value = data.textSuggestions.map((s) => {
        const text = s.text;
        return {
          suggestion: text,
          label: text.replaceAll(searchParameter, `<span class="text-primary">${searchParameter}</span>`),
          isFromHistory: s.isFromHistory,
          select: async () => {
            isOpenSearchSuggestions.value = false;
            trackCustomEvent("search-suggestion-select", searchParameter);
            await executeSearch(text);
          },
        };
      });

      searchSuggestionSelectedIndex.value = -1;
    }

    isOpenSearchSuggestions.value = true;
    trackCustomEvent("search-suggestion-fetch", searchParameter);
  }
  catch (error) {
    console.error("Error fetching search suggestions:", error);
    useFailToast(t("common.title.error"), t("search-suggestions.message.error"));
  }
  finally {
    searchSuggestionStatus.value = "idle";
  }
}

const debouncedFetchData = useDebounceFn(fetchData, 500, { maxWait: 5000 });

function onFocusSearchTerm() {
  if (!isOpenSearchSuggestions.value && searchTerm?.value) {
    fetchData();
  }
}

function onInputSearchTerm(event: any) {
  searchTerm.value = (event.target as HTMLInputElement).value;
  debouncedFetchData();
}

function onKeySearchTerm(event: KeyboardEvent) {
  switch (event.code) {
    case "ArrowDown":
      event.preventDefault();
      event.stopPropagation();
      searchSuggestionSelectedIndex.value = 0;
      (searchSuggestionsContainer.value! as HTMLElement).focus();
      break;

    case "Enter":
      // when typing and pressing "enter" (too fast) without explicit handler, search term is not picked up before reloading
      event.preventDefault();
      event.stopPropagation();
      onSubmit();
      break;
  }
}

function onKeySearchSuggestions(event: KeyboardEvent) {
  switch (event.code) {
    case "ArrowDown":
      if (searchSuggestionSelectedIndex.value < searchSuggestionList.value.length - 1) {
        searchSuggestionSelectedIndex.value = searchSuggestionSelectedIndex.value + 1;
      }
      break;

    case "ArrowUp":
      if (searchSuggestionSelectedIndex.value === 0) {
        (searchTermInput.value as any).$refs.input.focus();
      }
      else if (searchSuggestionSelectedIndex.value > 0) {
        searchSuggestionSelectedIndex.value = searchSuggestionSelectedIndex.value - 1;
      }
      break;

    case "Enter":
      searchSuggestionList.value[searchSuggestionSelectedIndex.value].select();
      break;
  }
}

async function onSubmit() {
  searchSuggestionList.value = [];
  await executeSearch(searchTerm.value);
}

async function executeSearch(term: string) {
  if (!term || term.trim().length < 3) {
    useWarningToast(t("common.title.warning"), t("search.message.warning.invalid-term"));
    return;
  }

  searchTermStatus.value = "pending";

  const redirectUrl = localePath(`/catalog?term=${encodeURIComponent(term)}`);
  window.location.href = redirectUrl;
}
</script>

<template>
  <form id="form-header-search" ref="searchForm" role="search" method="get" @submit.prevent="onSubmit">
    <div class="flex w-full rounded-full border border-neutral-100 bg-white p-1 lg:w-87.5 xl:w-125">
      <input
        ref="searchTermInput" v-model="searchTerm" type="search" data-1p-ignore
        class="me-xl ms-md w-full grow bg-white text-neutral-300 placeholder:text-[#00103599] focus:outline-none"
        aria-autocomplete="both" aria-owns="search-suggestions" tabindex="0"
        :placeholder="t('search.placeholder')"
        :aria-expanded="isOpenSearchSuggestions"
        @focus="onFocusSearchTerm"
        @input="onInputSearchTerm"
        @keydown="onKeySearchTerm"
      >

      <div class="flex-none">
        <UButton variant="solid" color="primary" size="md" type="submit" icon="i-ph-magnifying-glass" :ui="{ base: 'gap-x-2.5! p-2.5! leading-6' }" />
      </div>
    </div>

    <div
      v-show="isOpenSearchSuggestions && searchSuggestionList.length > 0" ref="searchSuggestionsContainer"
      tabindex="1" class="mt-2xs lg:mt-xs absolute w-full rounded-md border border-neutral-100
      bg-white focus-visible:outline-none lg:rounded-xl"
      @keydown.prevent="onKeySearchSuggestions"
    >
      <ul id="search-suggestions" class="flex flex-col" role="listbox">
        <li
          v-for="(searchSuggestion, idx) in searchSuggestionList"
          :key="searchSuggestion.label"
          class="border-b border-neutral-100
          first:rounded-t-md last:rounded-b-md last:border-0 hover:bg-[#F8F7F4] lg:first:rounded-t-xl lg:last:rounded-b-xl hover:[&_p]:underline"
          :class="{ 'bg-[#F8F7F4]': searchSuggestionSelectedIndex === idx, '[&_p]:underline': searchSuggestionSelectedIndex === idx }"
        >
          <button
            type="button"
            class="p-sm w-full cursor-pointer text-left focus:outline-none"
            @click="searchSuggestion.select"
          >
            <div class="flex items-center justify-between">
              <div class="line-clamp-1 text-[16px] font-medium leading-6" :title="searchSuggestion.suggestion" v-html="searchSuggestion.label" />
              <div class="flex items-center">
                <UIcon v-if="searchSuggestion.isFromHistory" name="i-ph-clock-counter-clockwise" class="text-neutral-400" />
              </div>
            </div>
          </button>
        </li>
      </ul>
    </div>
  </form>
</template>

<style scoped>

</style>
